import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileSpreadsheet, FileText, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { downloadCsv } from '@/lib/csv';
import { downloadXlsx } from '@/lib/xlsx';
import { useOmlMutations } from '@/hooks/useOml';
import { RequestAccessDialog } from './LockedField';
import type { OmlChildType } from '../../../../shared/types/oml';

/**
 * Exportar (§12.5): CSV / XLSX de lo filtrado y visible, PDF de la ficha. El
 * botón solo aparece para quien puede exportar (AC-18); el resto ve "Pedir
 * permiso de exportación". Las filas las arma el backend (y registra la
 * exportación); acá solo se serializa el archivo.
 */
export function ExportMenu({ canExport, scope, accountId, accountIds, entityType, entityIds, filters, size = 'sm' }: {
  canExport: boolean;
  scope: 'accounts' | 'entities' | 'account';
  accountId?: string;
  accountIds?: string[];
  entityType?: OmlChildType;
  entityIds?: string[];
  filters?: Record<string, unknown>;
  size?: 'sm' | 'default';
}) {
  const { t } = useTranslation();
  const { exportAccounts, exportEntities, accountPdf } = useOmlMutations();
  const [askOpen, setAskOpen] = useState(false);
  const busy = exportAccounts.isPending || exportEntities.isPending || accountPdf.isPending;

  const run = async (format: 'csv' | 'xlsx') => {
    const res = scope === 'entities' && accountId && entityType
      ? await exportEntities.mutateAsync({ accountId, type: entityType, ids: entityIds, format })
      : await exportAccounts.mutateAsync({ accountIds: accountIds?.length ? accountIds : [accountId!], format, filters });
    if (format === 'csv') downloadCsv(res.filename, res.headers, res.rows);
    else await downloadXlsx(res.filename, res.headers, res.rows, { sheet: 'Owned Media' });
  };
  const pdf = async () => {
    if (!accountId) return;
    const r = await accountPdf.mutateAsync({ accountId });
    window.open(r.url, '_blank', 'noopener');
  };

  if (!canExport) {
    return (
      <>
        <Button size={size} variant="ghost" className="text-muted-foreground" onClick={() => setAskOpen(true)}><Lock className="h-3.5 w-3.5 mr-1" />{t('oml.export.ask', 'Pedir permiso de exportación')}</Button>
        {askOpen && <RequestAccessDialog open={askOpen} onOpenChange={setAskOpen} accountId={accountId ?? null} scope="export" />}
      </>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size={size} variant="outline" disabled={busy}><Download className="h-3.5 w-3.5 mr-1" />{t('oml.export.button', 'Exportar')}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => run('csv')}><FileText className="h-4 w-4 mr-2" />CSV</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run('xlsx')}><FileSpreadsheet className="h-4 w-4 mr-2" />XLSX</DropdownMenuItem>
        {(scope === 'account' || (scope === 'entities' && accountId)) && <DropdownMenuItem onClick={pdf}><FileText className="h-4 w-4 mr-2" />{t('oml.export.pdf', 'PDF de la ficha')}</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
